import * as React from 'react';
import { PageSection, Title, Card, CardBody, CardTitle, CardFooter, Button, Modal, ModalVariant, Wizard, WizardStep, WizardHeader, List, ListItem, Alert } from '@patternfly/react-core';
import ExternalLinkSquareAltIcon from '@patternfly/react-icons/dist/esm/icons/external-link-square-alt-icon';
import { useNavigate, useLocation } from 'react-router-dom';
import { AUTH_CONFIG, PARTNER_ID, PARTNER_DISPLAY_NAME, SUPPORT_EMAIL, PRODUCT_NAME } from '../../config/partner';

const entraUrl = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?prompt=consent&client_id=${AUTH_CONFIG.ENTRA_CLIENT_ID}&response_mode=form_post&response_type=code%20id_token&nonce=KEtXs2KwH6&sso_reload=true&scope=openid%20profile%20https%3A%2F%2Fgraph.microsoft.com%2F.default&state=graph-${PARTNER_ID}`;
const defenderUrl = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?prompt=consent&client_id=${AUTH_CONFIG.DEFENDER_CLIENT_ID}&response_mode=form_post&response_type=code%20id_token&nonce=KEtXs2KwH6&sso_reload=true&scope=openid%20profile%20https%3A%2F%2Fgraph.microsoft.com%2F.default&state=defender-${PARTNER_ID}`;
const azureUrl1 = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?prompt=consent&client_id=${AUTH_CONFIG.AZURE_CLIENT_ID}&response_mode=form_post&response_type=code%20id_token&nonce=KEtXs2KwH6&sso_reload=true&scope=openid%20profile&state=reg-${PARTNER_ID}`;
const azureUrl2 = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?prompt=consent&client_id=${AUTH_CONFIG.AZURE_CLIENT_ID}&response_mode=form_post&response_type=code%20id_token&nonce=pX0ZckTXA3&sso_reload=true&scope=openid%20https%3A%2F%2Fmanagement.azure.com%2F.default&state=auth-${PARTNER_ID}`;

const logoutUrl = "https://login.microsoftonline.com/common/oauth2/v2.0/logout";

const Home: React.FunctionComponent = () => {
  const [isEntraModalOpen, setIsEntraModalOpen] = React.useState(false);
  const [isDefenderModalOpen, setIsDefenderModalOpen] = React.useState(false);
  const [isAzureModalOpen, setIsAzureModalOpen] = React.useState(false);
  const [entraStep, setEntraStep] = React.useState(1);
  const [defenderStep, setDefenderStep] = React.useState(1);
  const [azureStep, setAzureStep] = React.useState(1);
  const [hasLoggedOutEntra, setHasLoggedOutEntra] = React.useState(false);
  const [hasLoggedOutDefender, setHasLoggedOutDefender] = React.useState(false);
  const [hasLoggedOutAzure, setHasLoggedOutAzure] = React.useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Handle URL changes for Entra wizard
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const stepParam = params.get('entraStep');
    if (stepParam) {
      const stepNumber = parseInt(stepParam, 10);
      if (!isNaN(stepNumber) && stepNumber >= 1 && stepNumber <= 5) {
        setEntraStep(stepNumber);
        setIsEntraModalOpen(true);
      }
    }
  }, [location]);

  // Handle URL changes for Defender wizard
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const stepParam = params.get('defenderStep');
    if (stepParam) {
      const stepNumber = parseInt(stepParam, 10);
      if (!isNaN(stepNumber) && stepNumber >= 1 && stepNumber <= 5) {
        setDefenderStep(stepNumber);
        setIsDefenderModalOpen(true);
      }
    }
  }, [location]);

  // Handle URL changes for Azure wizard
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const stepParam = params.get('azureStep');
    if (stepParam) {
      const stepNumber = parseInt(stepParam, 10);
      if (!isNaN(stepNumber) && stepNumber >= 1 && stepNumber <= 7) {
        setAzureStep(stepNumber);
        setIsAzureModalOpen(true);
      }
    }
  }, [location]);

  const handleEntraStepChange = (event: any, currentStep: any) => {
    const newStep = Number(currentStep.id);
    setEntraStep(newStep);
    navigate(`${location.pathname}?entraStep=${newStep}`, { replace: true });
  };

  const handleDefenderStepChange = (event: any, currentStep: any) => {
    const newStep = Number(currentStep.id);
    setDefenderStep(newStep);
    navigate(`${location.pathname}?defenderStep=${newStep}`, { replace: true });
  };

  const handleAzureStepChange = (event: any, currentStep: any) => {
    const newStep = Number(currentStep.id);
    setAzureStep(newStep);
    navigate(`${location.pathname}?azureStep=${newStep}`, { replace: true });
  };

  const handleCloseEntraModal = () => {
    setIsEntraModalOpen(false);
    navigate(location.pathname, { replace: true });
  };

  const handleCloseDefenderModal = () => {
    setIsDefenderModalOpen(false);
    navigate(location.pathname, { replace: true });
  };

  const handleCloseAzureModal = () => {
    setIsAzureModalOpen(false);
    navigate(location.pathname, { replace: true });
  };

  return (
    <PageSection hasBodyWrapper={false}>
      <Alert
        variant="info"
        title="Your Security is Our Priority"
        isInline
      >
        <p>All data collected during security assessments is protected by AES-256 encryption and stored in a logically isolated, highly restricted environment. Our security controls meet or exceed industry standards, and we maintain strict access controls and audit logging for all operations.</p>
      </Alert>
      <Title headingLevel="h1" size="xl">{PRODUCT_NAME} Services</Title>
      <p>{PRODUCT_NAME} provides an easy, fast way for {PARTNER_DISPLAY_NAME} to help assess the security of your environment(s).</p>

      <p>With just a few clicks, you can get a report on your protection status and key risks.<br/>
      Checks include Users, Multi-factor Authentication, O365 Security, Entra and Azure.</p>

      <p>Select one of the {PRODUCT_NAME} options below to perform an assessment.</p>

      <Card>
        <CardTitle>Combined {PRODUCT_NAME} - Microsoft Entra and MS365</CardTitle>
        <CardBody>
          Review all your users in Entra ID to rapidly surface high-risk issues such as unused accounts, or accounts without MFA.
          <p>Review all your assets to identify dormant assets, and those which are not protected/onboarded to Microsoft bundled tools such as Intune, etc.</p>
          <p>Review your MS365 security.</p>
          <p>You will receive a report highlighting assets and users, their protection status and all identified high risk items, along with remediation recommendations.</p>
          <p>The report also includes identified high-risk issues with the configuration of your MS365 environment(s), along with remediation recommendations</p>
        </CardBody>
        <CardFooter>
          <Button variant="primary" onClick={() => {
            setEntraStep(1);
            navigate(`${location.pathname}?entraStep=1`, { replace: true });
            setIsEntraModalOpen(true);
          }}>
            {PRODUCT_NAME} Entra and MS365
          </Button>
          <i>&nbsp;&nbsp;Check you are signed into the target Entra ID tenant with Admin Rights.</i>
        </CardFooter>
      </Card>

      <Card>
        <CardTitle>{PRODUCT_NAME} - Microsoft Defender</CardTitle>
        <CardBody>
          <p>Review all your assets to identify dormant assets, and those which are not protected/onboarded to Microsoft Defender.</p>
          <p>You will receive a report highlighting assets, their protection status and all identified high risk items, along with remediation recommendations.</p>
        </CardBody>
        <CardFooter>
          <Button variant="primary" onClick={() => {
            setDefenderStep(1);
            navigate(`${location.pathname}?defenderStep=1`, { replace: true });
            setIsDefenderModalOpen(true);
          }}>
            {PRODUCT_NAME} Defender
          </Button>
          <i>&nbsp;&nbsp;Check you are signed into the target Entra ID tenant with Admin Rights. Defender must be active within your tenant to use this check.</i>
        </CardFooter>
      </Card>

      <Card>
        <CardTitle>{PRODUCT_NAME} - Azure Security</CardTitle>
        <CardBody>
          Review your Azure security in just a few clicks.
          <p>You will receive a report showing your environment(s) and identified high-risk issues, along with remediation recommendations.</p>
        </CardBody>
        <CardFooter>
          <Button variant="primary" onClick={() => {
            setAzureStep(1);
            navigate(`${location.pathname}?azureStep=1`, { replace: true });
            setIsAzureModalOpen(true);
          }}>
            {PRODUCT_NAME} Azure
          </Button>
          <i>&nbsp;&nbsp;Check you are signed into the target Azure tenant with Admin Rights.</i>
        </CardFooter>
      </Card>

      {/* Entra Wizard Modal */}
      <Modal
        isOpen={isEntraModalOpen}
        variant={ModalVariant.medium}
      >
        <Wizard
          height={500}
          onClose={handleCloseEntraModal}
          title="Entra Wizard"
          startIndex={entraStep}
          onStepChange={handleEntraStepChange}
          isVisitRequired
          header={
            <WizardHeader
              onClose={handleCloseEntraModal}
              title={`Combined ${PRODUCT_NAME} - Microsoft Entra and MS365`}
              description="Follow these steps to perform a comprehensive security assessment of your environment."
            />
          }
        >
          <WizardStep name="Introduction" id="1">
            <List>
              <ListItem>Follow the link in the next step to be redirected to Microsoft.</ListItem>
              <ListItem>You will be prompted to review and accept to a number of permissions required for data collection.</ListItem>
              <ListItem>With the exception of 'Read and write all applications', each of the permissions requested are read-only and allow {PRODUCT_NAME} to collect data without a signed-in user.</ListItem>
              <ListItem>'Read and write all applications' is requested to allow {PRODUCT_NAME} to revoke it's own access when the checks are complete.</ListItem>
            </List>
          </WizardStep>
          <WizardStep name="Accept permissions" id="2" footer={{ isNextDisabled: true }}>
            <p>Click the button below to register the {PRODUCT_NAME} application in your environment.</p>
            <br/>
            <a href={entraUrl} rel="noopener noreferrer">
              <Button variant="primary" icon={<ExternalLinkSquareAltIcon />} iconPosition="end">{PRODUCT_NAME} Entra and MS365</Button>
            </a>
          </WizardStep>
          <WizardStep name="Application registered" id="3">
            <p>The {PRODUCT_NAME} application has been successfully registered in your environment.</p>
            <br/>
            <p>You can now proceed to the next step.</p>
          </WizardStep>
          <WizardStep name="Logout" id="4" footer={{ isNextDisabled: !hasLoggedOutEntra }}>
            <p>To revoke session cookies used during setup, please logout from Microsoft before proceeding:</p>
            <br/>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Button 
                variant="primary" 
                icon={<ExternalLinkSquareAltIcon />} 
                iconPosition="end"
                onClick={() => {
                  window.open(logoutUrl, 'Microsoft Logout', 'width=500,height=600,left=100,top=100');
                  setHasLoggedOutEntra(true);
                }}
              >
                Logout from Microsoft
              </Button>
              <p>(Opens in a new window)</p>
            </div>
          </WizardStep>
          <WizardStep
            name="Integration complete"
            id="5"
            footer={{ 
              nextButtonText: 'Finish', 
              onNext: handleCloseEntraModal
            }}
          >
            <b>Success!</b>
            <br/>
            <br/>
            Thank you for completing the registration process.
            <br/>
            <br/>
            Data collection will commence shortly.
            <br/>
            <br/>
            A member of the team will contact you as soon as your report is available.
            <br/>
            <br/>
            If you need any further assistance please <Button variant="primary" component="a" href={`mailto:${SUPPORT_EMAIL}?subject=${PRODUCT_NAME} support`} >Contact Support</Button>
          </WizardStep>
        </Wizard>
      </Modal>

      {/* Defender Wizard Modal */}
      <Modal
        isOpen={isDefenderModalOpen}
        variant={ModalVariant.medium}
      >
        <Wizard
          height={500}
          onClose={handleCloseDefenderModal}
          title="Defender Wizard"
          startIndex={defenderStep}
          onStepChange={handleDefenderStepChange}
          isVisitRequired
          header={
            <WizardHeader
              onClose={handleCloseDefenderModal}
              title={`${PRODUCT_NAME} - Microsoft Defender`}
              description="Follow these steps to perform a security assessment of your Defender environment."
            />
          }
        >
          <WizardStep name="Introduction" id="1">
            <List>
              <ListItem>Follow the link in the next step to be redirected to Microsoft.</ListItem>
              <ListItem>You will be prompted to review and accept to a number of permissions required for data collection.</ListItem>
              <ListItem>With the exception of 'Read and write all applications', each of the permissions requested are read-only and allow {PRODUCT_NAME} to collect data without a signed-in user.</ListItem>
              <ListItem>'Read and write all applications' is requested to allow {PRODUCT_NAME} to revoke it's own access when the checks are complete.</ListItem>
            </List>
          </WizardStep>
          <WizardStep name="Accept permissions" id="2" footer={{ isNextDisabled: true }}>
            <p>Click the button below to register the {PRODUCT_NAME} application in your environment.</p>
            <br/>
            <a href={defenderUrl} rel="noopener noreferrer">
              <Button variant="primary" icon={<ExternalLinkSquareAltIcon />} iconPosition="end">{PRODUCT_NAME} Defender</Button>
            </a>
          </WizardStep>
          <WizardStep name="Application registered" id="3">
            <p>The {PRODUCT_NAME} application has been successfully registered in your environment.</p>
            <br/>
            <p>You can now proceed to the next step.</p>
          </WizardStep>
          <WizardStep name="Logout" id="4" footer={{ isNextDisabled: !hasLoggedOutDefender }}>
            <p>To revoke session cookies used during setup, please logout from Microsoft before proceeding:</p>
            <br/>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Button 
                variant="primary" 
                icon={<ExternalLinkSquareAltIcon />} 
                iconPosition="end"
                onClick={() => {
                  window.open(logoutUrl, 'Microsoft Logout', 'width=500,height=600,left=100,top=100');
                  setHasLoggedOutDefender(true);
                }}
              >
                Logout from Microsoft
              </Button>
              <p>(Opens in a new window)</p>
            </div>
          </WizardStep>
          <WizardStep
            name="Integration complete"
            id="5"
            footer={{ 
              nextButtonText: 'Finish', 
              onNext: handleCloseDefenderModal
            }}
          >
            <b>Success!</b>
            <br/>
            <br/>
            Thank you for completing the registration process.
            <br/>
            <br/>
            Data collection will commence shortly.
            <br/>
            <br/>
            A member of the team will contact you as soon as your report is available.
            <br/>
            <br/>
            If you need any further assistance please <Button variant="primary" component="a" href={`mailto:${SUPPORT_EMAIL}?subject=${PRODUCT_NAME} support`} >Contact Support</Button>
          </WizardStep>
        </Wizard>
      </Modal>

      {/* Azure Wizard Modal */}
      <Modal
        isOpen={isAzureModalOpen}
        variant={ModalVariant.medium}
      >
        <Wizard
          height={600}
          onClose={handleCloseAzureModal}
          title="Azure Wizard"
          startIndex={azureStep}
          onStepChange={handleAzureStepChange}
          isVisitRequired
          header={
            <WizardHeader
              onClose={handleCloseAzureModal}
              title={`${PRODUCT_NAME} - Azure Security`}
              description="Follow these steps to perform a security assessment of your Azure environment."
            />
          }
        >
          <WizardStep name="Introduction" id="1">
            <List>
              <ListItem>Follow the link in step 2 to be redirected to Microsoft.</ListItem>
              <ListItem>You will be prompted to review and accept some basic, read-only permissions to install the {PRODUCT_NAME} app in your environment.</ListItem>
              <ListItem>Next follow the link in step 4.</ListItem>
              <ListItem>You will be prompted to review and accept short-term, delegated access to Azure Resource Manager using your credentials.</ListItem>
              <ListItem>{PRODUCT_NAME} will then attempt to assign the 'Reader' role for each of your Azure subscriptions to the {PRODUCT_NAME} service principal.</ListItem>
            </List>
          </WizardStep>
          <WizardStep name="Accept permissions" id="2" footer={{ isNextDisabled: true }}>
            <p>Click the button below to register the {PRODUCT_NAME} application in your environment.</p>
            <br/>
            <a href={azureUrl1} rel="noopener noreferrer">
              <Button variant="primary" icon={<ExternalLinkSquareAltIcon />} iconPosition="end">{PRODUCT_NAME} Azure</Button>
            </a>
          </WizardStep>
          <WizardStep name="Application registered" id="3">
            <p>The {PRODUCT_NAME} application has been successfully registered in your environment.</p>
            <br/>
            <p>You can now proceed to the next step.</p>
          </WizardStep>
          <WizardStep name="Detect subscriptions" id="4" footer={{ isNextDisabled: true }}>
            <p>Click the button below to detect your Azure subscriptions and assign the 'Reader' role to the {PRODUCT_NAME} application.</p>
            <br/>
            <a href={azureUrl2} rel="noopener noreferrer">
              <Button variant="primary" icon={<ExternalLinkSquareAltIcon />} iconPosition="end">Detect Azure Subscriptions</Button>
            </a>
          </WizardStep>
          <WizardStep name="Read access granted" id="5">
            <p>The {PRODUCT_NAME} application has been successfully assigned the 'Reader' role on each of the detected Azure subscriptions.</p>
            <br/>
            <p>You can now proceed to the next step.</p>
          </WizardStep>
          <WizardStep name="Logout" id="6" footer={{ isNextDisabled: !hasLoggedOutAzure }}>
            <p>To revoke session cookies used during setup, please logout from Microsoft before proceeding:</p>
            <br/>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Button 
                variant="primary" 
                icon={<ExternalLinkSquareAltIcon />} 
                iconPosition="end"
                onClick={() => {
                  window.open(logoutUrl, 'Microsoft Logout', 'width=500,height=600,left=100,top=100');
                  setHasLoggedOutAzure(true);
                }}
              >
                Logout from Microsoft
              </Button>
              <p>(Opens in a new window)</p>
            </div>
          </WizardStep>
          <WizardStep
            name="Integration complete"
            id="7"
            footer={{ 
              nextButtonText: 'Finish', 
              onNext: handleCloseAzureModal
            }}
          >
            <b>Success!</b>
            <br/>
            <br/>
            Thank you for completing the registration process.
            <br/>
            <br/>
            Data collection will commence shortly.
            <br/>
            <br/>
            A member of the team will contact you as soon as your report is available.
            <br/>
            <br/>
            If you need any further assistance please <Button variant="primary" component="a" href={`mailto:${SUPPORT_EMAIL}?subject=${PRODUCT_NAME} support`} >Contact Support</Button>
          </WizardStep>
        </Wizard>
      </Modal>
    </PageSection>
  );
};

export { Home };
